import { Injectable, inject } from '@angular/core';
import { ChatAttachment } from '../models/chat-message';
import { FileTextService } from './file-text.service';

/** Longest image edge kept after downscaling; keeps stored data URLs small. */
const MAX_IMAGE_EDGE = 1024;
const JPEG_QUALITY = 0.82;
// Safari commonly supplies camera photos as `image/heic` or `image/heif`.
// Decode any browser-recognised image and re-encode it as JPEG below, rather
// than dropping formats that are not universally supported by vision APIs.
const IMAGE_TYPE = /^image\//i;
/** Cap on extracted file text kept per attachment; keeps chat payloads and prompts bounded. */
const MAX_FILE_TEXT_CHARS = 60_000;

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private readonly fileText = inject(FileTextService);

  /**
   * Builds chat attachments from raw files. Images are downscaled and stored as
   * JPEG data URLs; common text, code, docx, and pdf files are read to a text
   * payload; anything else is returned name-only so it can still be shown.
   */
  async createAttachments(files: Array<File | Blob>): Promise<ChatAttachment[]> {
    const attachments: ChatAttachment[] = [];
    for (const file of files) {
      const attachment = await this.fromFile(file);
      if (attachment) attachments.push(attachment);
    }
    return attachments;
  }

  private async fromFile(file: File | Blob): Promise<ChatAttachment | null> {
    const name = file instanceof File ? file.name || 'file' : 'file';
    const isImage = IMAGE_TYPE.test(file.type);

    if (isImage) {
      try {
        const { dataUrl, width, height } = await this.downscaleImage(file);
        return this.withSourceFile({ id: this.createId(), name, mime: 'image/jpeg', dataUrl, width, height }, file);
      } catch {
        return this.withSourceFile({ id: this.createId(), name, mime: file.type || 'image', dataUrl: undefined }, file);
      }
    }

    if (file instanceof File) {
      const text = await this.fileText.extractText(file, MAX_FILE_TEXT_CHARS);
      if (text) {
        return this.withSourceFile({ id: this.createId(), name, mime: file.type || 'file', text }, file);
      }
    }
    return this.withSourceFile({ id: this.createId(), name, mime: file.type || 'file' }, file);
  }

  private downscaleImage(file: File | Blob): Promise<{ dataUrl: string; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        try {
          const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
          const width = Math.max(1, Math.round(image.naturalWidth * scale));
          const height = Math.max(1, Math.round(image.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Canvas 2D context unavailable');
          // Paint white first so transparent PNGs do not turn black as JPEG.
          context.fillStyle = '#fff';
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0, width, height);
          resolve({ dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY), width, height });
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read image'));
      };
      image.src = objectUrl;
    });
  }

  private createId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `attach-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** Keep the original only in memory; message persistence must never JSON-encode a File. */
  private withSourceFile(attachment: ChatAttachment, file: File | Blob): ChatAttachment {
    if (file instanceof File) Object.defineProperty(attachment, 'sourceFile', { value: file, enumerable: false });
    return attachment;
  }
}
